import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Phone, Star, Shield, Heart, Award, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import InstagramFeed from '../components/InstagramFeed';
import TrustedReviews from '../components/TrustedReviews';
import { BUSINESS_INFO } from '../data/reviewSources';

// Featured puppies data (static)
const FEATURED_PUPPIES = [
  {
    id: "1",
    breed: "Golden Retriever",
    age: "8-12 weeks",
    description: "Friendly, intelligent, and devoted. Perfect family companion with gentle temperament.",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/3bjx16yv_Golden%20Retriever.jpeg"]
  },
  {
    id: "2",
    breed: "Shih Tzu",
    age: "8-12 weeks",
    description: "Affectionate, playful, and outgoing. Excellent apartment dog with beautiful coat.",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/adz6fgom_Shih%20Tzus.jpg"]
  },
  {
    id: "3",
    breed: "Labrador",
    age: "8-12 weeks",
    description: "Outgoing, even-tempered, and gentle. Most popular family dog in the world.",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/np0pkanr_Labrador.jpeg"]
  }
];

const Home = () => {
  const handleWhatsApp = () => {
    const message = 'Hello, I want to know about available puppies at Petsmart Puppies.';
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Healthy Puppies',
      description: 'All puppies are vaccinated and health-certified'
    },
    {
      icon: Heart,
      title: 'Pure Breed Assurance',
      description: 'Lifetime breed guarantee for your peace of mind'
    },
    {
      icon: Award,
      title: 'Expert Guidance',
      description: 'Professional advice on care, training, and nutrition'
    }
  ];

  return (
    <>
      <SEO path="/" />

      <div data-testid="home-page">
        {/* Hero Section */}
        <section
          className="py-12 sm:py-16 lg:py-20"
          style={{
            background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF, #FEF3C7)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1
                  className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6"
                  style={{ color: 'var(--text-main)', lineHeight: '1.1' }}
                >
                  Pet Shop & Puppies in Noida
                </h1>
                <p
                  className="text-base sm:text-lg mb-6 sm:mb-8"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Your trusted destination for healthy puppies, dog food, and pet accessories in Sector 31 Noida.
                </p>

                {/* Trust Badge */}
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 sm:mb-8"
                  style={{ backgroundColor: 'var(--primary-light)' }}
                >
                  <Star size={20} style={{ color: '#F59E0B', fill: '#F59E0B' }} />
                  <span style={{ color: 'var(--primary-dark)', fontWeight: '600' }}>
                    4.6 Google Rating
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>• 199+ Reviews</span>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <Link
                    to="/puppies/"
                    data-testid="view-puppies-button"
                    className="transition-all hover:-translate-y-1 text-center"
                    style={{
                      backgroundColor: 'var(--primary-main)',
                      color: 'white',
                      padding: '0.875rem 1.5rem',
                      borderRadius: '9999px',
                      fontWeight: 'bold',
                      boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    View Puppies <ArrowRight size={20} />
                  </Link>
                  <button
                    onClick={handleWhatsApp}
                    data-testid="whatsapp-cta-button"
                    className="transition-all hover:-translate-y-1"
                    style={{
                      backgroundColor: '#25D366',
                      color: 'white',
                      padding: '0.875rem 1.5rem',
                      borderRadius: '9999px',
                      fontWeight: 'bold',
                      boxShadow: '0 10px 30px rgba(37, 211, 102, 0.3)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <MessageCircle size={20} /> Chat on WhatsApp
                  </button>
                  <a
                    href="tel:+917217648679"
                    data-testid="call-cta-button"
                    className="transition-all hover:-translate-y-1 text-center"
                    style={{
                      backgroundColor: 'white',
                      color: 'var(--primary-main)',
                      padding: '0.875rem 1.5rem',
                      borderRadius: '9999px',
                      fontWeight: 'bold',
                      border: '2px solid var(--primary-main)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Phone size={20} /> Call Now
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/rzrk4eb7_main%20banner%20image.jpeg"
                  alt="Adorable puppies at Petsmart Puppies Noida"
                  className="shadow-soft w-full"
                  style={{ borderRadius: '1.5rem', maxHeight: '450px', objectFit: 'cover' }}
                  loading="eager"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 sm:py-16 lg:py-20" style={{ backgroundColor: 'white' }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
                Why Choose Petsmart Puppies?
              </h2>
              <p style={{ color: 'var(--text-muted)' }}>Trusted by hundreds of pet parents across Noida</p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
              {whyChooseUs.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center p-6 sm:p-8"
                    style={{
                      backgroundColor: 'var(--neutral-bg)',
                      borderRadius: '1.5rem'
                    }}
                  >
                    <div
                      className="inline-flex items-center justify-center mb-4"
                      style={{
                        width: '70px',
                        height: '70px',
                        backgroundColor: 'var(--primary-light)',
                        borderRadius: '50%'
                      }}
                    >
                      <Icon size={32} style={{ color: 'var(--primary-main)' }} />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm sm:text-base" style={{ color: 'var(--text-muted)' }}>{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Puppies */}
        <section className="py-12 sm:py-16 lg:py-20" style={{ backgroundColor: 'var(--neutral-bg)' }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
                Featured Puppies
              </h2>
              <p style={{ color: 'var(--text-muted)' }}>Meet our adorable puppies looking for loving homes</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="featured-puppies-list">
              {FEATURED_PUPPIES.map((puppy, index) => (
                <motion.div
                  key={puppy.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="shadow-soft transition-all hover:shadow-xl group"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '1.5rem',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <img
                      src={puppy.images[0]}
                      alt={`${puppy.breed} puppy available at Petsmart Puppies Noida`}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '240px',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      className="group-hover:scale-110"
                    />
                    {puppy.vaccinated && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '0.75rem',
                          right: '0.75rem',
                          backgroundColor: 'var(--accent-main)',
                          color: 'white',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        Vaccinated
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      {puppy.breed}
                    </h3>
                    <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                      Age: {puppy.age}
                    </p>
                    <p className="text-sm mb-4" style={{ color: 'var(--text-main)' }}>
                      {puppy.description}
                    </p>
                    <button
                      onClick={handleWhatsApp}
                      data-testid={`reserve-puppy-${puppy.id}`}
                      className="w-full transition-all hover:-translate-y-1"
                      style={{
                        backgroundColor: 'var(--primary-main)',
                        color: 'white',
                        padding: '0.75rem',
                        borderRadius: '0.75rem',
                        fontWeight: 'bold',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      Reserve Puppy
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-10 sm:mt-12">
              <Link
                to="/puppies/"
                className="inline-flex items-center gap-2 transition-all hover:-translate-y-1"
                style={{
                  backgroundColor: 'var(--primary-main)',
                  color: 'white',
                  padding: '0.875rem 1.75rem',
                  borderRadius: '9999px',
                  fontWeight: 'bold',
                  boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)'
                }}
              >
                View All Puppies <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Instagram Feed Section */}
        <InstagramFeed />

        {/* Trusted Reviews Section (Google + Justdial) */}
        <TrustedReviews />

        {/* Blog Teaser */}
        <section className="py-12 sm:py-16" style={{ backgroundColor: 'white' }}>
          <div className="container mx-auto px-4">
            <div 
              className="text-center p-8 sm:p-12 rounded-2xl"
              style={{ backgroundColor: 'var(--primary-light)' }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
                Pet Care Tips & Guides
              </h2>
              <p className="mb-6 max-w-2xl mx-auto" style={{ color: 'var(--text-muted)' }}>
                Read our expert articles on puppy care, breed guides, training tips, and more
              </p>
              <Link
                to="/blog/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:-translate-y-1"
                style={{
                  backgroundColor: 'var(--primary-main)',
                  color: 'white',
                  boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)'
                }}
              >
                Read Our Blog <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
